import React, { useState, useEffect, useCallback } from 'react';
import ReactDOM from 'react-dom';
import { useAuth } from '../../hooks/useAuth';
import { useTenant } from '../../hooks/useTenant';
import {
    Users, UserPlus, Shield, Building2, Trash2, Eye, EyeOff,
    X, Check, AlertCircle, Search, RotateCw, PlusCircle, Wifi, WifiOff,
    Settings, MessageSquare, Zap, Copy, ExternalLink, Globe, Database
} from 'lucide-react';
import CreateSedeModal from './CreateSedeModal';

let envApiUrl = process.env.REACT_APP_API_URL;
if (envApiUrl && envApiUrl.includes('localhost') && window.location.hostname !== 'localhost') {
    envApiUrl = envApiUrl.replace('localhost', window.location.hostname);
}
const API_URL = envApiUrl ||
    (process.env.NODE_ENV === 'production' ? window.location.origin : `http://${window.location.hostname}:4000`);

// ─── Helpers ──────────────────────────────────────────────────────────────────

const ROLE_LABELS = { SUPER_ADMIN: 'Super Admin', SEDE_ADMIN: 'Admin Sede', OPERATOR: 'Operador' };
const ROLE_COLORS = {
    SUPER_ADMIN: { bg: '#ede9fe', color: '#7c3aed' },
    SEDE_ADMIN: { bg: '#dbeafe', color: '#1d4ed8' },
    OPERATOR: { bg: '#f3f4f6', color: '#374151' },
};

const RoleBadge = ({ role }) => {
    const { bg, color } = ROLE_COLORS[role] || ROLE_COLORS.OPERATOR;
    return (
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', padding: '3px 10px', borderRadius: '999px', fontSize: '12px', fontWeight: 600, backgroundColor: bg, color }}>
            <Shield size={11} />{ROLE_LABELS[role] || role}
        </span>
    );
};

const StatusBadge = ({ isActive }) => (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', padding: '3px 10px', borderRadius: '999px', fontSize: '12px', fontWeight: 600, backgroundColor: isActive ? '#dcfce7' : '#fee2e2', color: isActive ? '#16a34a' : '#dc2626' }}>
        <span style={{ width: '6px', height: '6px', borderRadius: '50%', backgroundColor: isActive ? '#16a34a' : '#dc2626' }} />
        {isActive ? 'Activo' : 'Inactivo'}
    </span>
);

const ToggleSwitch = ({ checked, onChange, disabled }) => (
    <button onClick={() => !disabled && onChange(!checked)} style={{ width: '40px', height: '22px', borderRadius: '11px', border: 'none', cursor: disabled ? 'not-allowed' : 'pointer', backgroundColor: checked ? '#10b981' : '#d1d5db', position: 'relative', transition: 'background-color 0.2s', flexShrink: 0, opacity: disabled ? 0.5 : 1 }}>
        <span style={{ position: 'absolute', top: '3px', left: checked ? '21px' : '3px', width: '16px', height: '16px', borderRadius: '50%', backgroundColor: 'white', transition: 'left 0.2s', boxShadow: '0 1px 3px rgba(0,0,0,0.2)' }} />
    </button>
);

// ─── Create User Modal ────────────────────────────────────────────────────────
const CreateUserModal = ({ tenants, callerRole, callerTenants, currentSede, onClose, onCreated, showToast }) => {
    const { token } = useAuth();

    // Calculate available sedes first
    const availableSedesForCreate = callerRole === 'SUPER_ADMIN'
        ? tenants
        : tenants.filter(t => callerTenants.some(ct => ct.slug === t.slug));

    // SEDE_ADMIN: if they have ONLY 1 sede available, hide field and preset it.
    const hideSedeField = callerRole === 'SEDE_ADMIN' && availableSedesForCreate.length === 1;
    const sedeAdminPreset = hideSedeField ? availableSedesForCreate[0].slug : '';

    const [form, setForm] = useState({ 
        username: '', 
        password: '', 
        name: '', 
        email: '', 
        role: 'OPERATOR', 
        sede: hideSedeField ? [sedeAdminPreset] : (currentSede ? [currentSede] : []) 
    });
    const [showPass, setShowPass] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    // Keep sede in sync if currentSede changes (e.g. panel filter changes while modal is open)
    useEffect(() => {
        if (!hideSedeField && form.sede.length === 0) {
            setForm(f => ({ ...f, sede: currentSede ? [currentSede] : [] }));
        }
    }, [currentSede, hideSedeField, form.sede.length]);

    // SEDE_ADMIN can create OPERATORs and other SEDE_ADMINs
    const availableRoles = callerRole === 'SUPER_ADMIN'
        ? [{ v: 'OPERATOR', l: 'Operador' }, { v: 'SEDE_ADMIN', l: 'Admin Sede' }, { v: 'SUPER_ADMIN', l: 'Super Admin' }]
        : [{ v: 'OPERATOR', l: 'Operador' }, { v: 'SEDE_ADMIN', l: 'Admin Sede' }];

    const fixedSedeName = hideSedeField
        ? (availableSedesForCreate[0]?.name || availableSedesForCreate[0]?.slug)
        : null;

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError(null);
        setLoading(true);
        try {
            const res = await fetch(`${API_URL}/api/admin/users`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
                body: JSON.stringify(form)
            });
            const data = await res.json();
            if (!res.ok) { setError(data.error || 'Error al crear usuario'); setLoading(false); return; }
            showToast('Usuario creado exitosamente', 'success');
            onCreated(data.user);
            onClose();
        } catch {
            setError('Error de conexión');
            setLoading(false);
        }
    };

    const inputStyle = { width: '100%', padding: '9px 12px', border: '1px solid #d1d5db', borderRadius: '8px', fontSize: '14px', outline: 'none', boxSizing: 'border-box', background: 'white', minWidth: 0 };
    const labelStyle = { display: 'block', fontSize: '13px', fontWeight: '600', color: '#374151', marginBottom: '5px' };
    const cellStyle = { minWidth: 0, overflow: 'hidden' };

    return (
        <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9000, padding: '16px' }} onClick={onClose}>
            <div style={{ background: 'white', borderRadius: '16px', width: '100%', maxWidth: '460px', boxShadow: '0 25px 50px -12px rgba(0,0,0,0.25)', overflow: 'hidden', maxHeight: '90vh', display: 'flex', flexDirection: 'column' }} onClick={e => e.stopPropagation()}>

                {/* Header */}
                <div style={{ padding: '20px 24px', background: 'linear-gradient(135deg,#1e1b4b,#4338ca)', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <UserPlus size={20} />
                        <span style={{ fontWeight: 700, fontSize: '17px' }}>Nuevo Usuario</span>
                    </div>
                    <button onClick={onClose} style={{ background: 'rgba(255,255,255,0.2)', border: 'none', borderRadius: '8px', padding: '4px 8px', cursor: 'pointer', color: 'white' }}>
                        <X size={18} />
                    </button>
                </div>

                <div style={{ overflowY: 'auto', flex: 1 }}>
                    {/* Decoy fields to prevent browser autofill */}
                    <div style={{ display: 'none' }} aria-hidden="true">
                        <input type="text" tabIndex={-1} readOnly />
                        <input type="password" tabIndex={-1} readOnly />
                    </div>

                    <form onSubmit={handleSubmit} autoComplete="off" style={{ padding: '24px', display: 'flex', flexDirection: 'column', gap: '14px' }}>
                        {error && (
                            <div style={{ background: '#fee2e2', border: '1px solid #fecaca', borderRadius: '8px', padding: '10px 12px', color: '#b91c1c', fontSize: '13px', display: 'flex', gap: '8px', alignItems: 'center' }}>
                                <AlertCircle size={15} />{error}
                            </div>
                        )}

                        {/* Nombre */}
                        <div style={cellStyle}>
                            <label style={labelStyle}>Nombre Completo *</label>
                            <input style={inputStyle} required autoComplete="off" value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} placeholder="Ej: Ana García" />
                        </div>

                        {/* Usuario + Email */}
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', minWidth: 0 }}>
                            <div style={cellStyle}>
                                <label style={labelStyle}>Usuario *</label>
                                <input style={inputStyle} required autoComplete="off" value={form.username} onChange={e => setForm(f => ({ ...f, username: e.target.value }))} placeholder="usuario" />
                            </div>
                            <div style={cellStyle}>
                                <label style={labelStyle}>Email</label>
                                <input style={inputStyle} type="email" autoComplete="off" value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} placeholder="correo@ejemplo.com" />
                            </div>
                        </div>

                        {/* Contraseña */}
                        <div style={cellStyle}>
                            <label style={labelStyle}>Contraseña *</label>
                            <div style={{ position: 'relative' }}>
                                <input style={{ ...inputStyle, paddingRight: '40px' }} type={showPass ? 'text' : 'password'} required autoComplete="new-password" value={form.password} onChange={e => setForm(f => ({ ...f, password: e.target.value }))} placeholder="Mín. 6 caracteres" />
                                <button type="button" onClick={() => setShowPass(!showPass)} style={{ position: 'absolute', right: '10px', top: '9px', background: 'none', border: 'none', cursor: 'pointer', color: '#6b7280' }}>
                                    {showPass ? <EyeOff size={16} /> : <Eye size={16} />}
                                </button>
                            </div>
                        </div>

                        {/* Rol + Sede */}
                        <div style={{ display: 'grid', gridTemplateColumns: hideSedeField ? '1fr' : '1fr 1fr', gap: '12px', minWidth: 0 }}>
                            <div style={cellStyle}>
                                <label style={labelStyle}>Rol *</label>
                                <select style={inputStyle} required value={form.role} onChange={e => setForm(f => ({ ...f, role: e.target.value }))}>
                                    {availableRoles.map(r => <option key={r.v} value={r.v}>{r.l}</option>)}
                                </select>
                            </div>
                            {/* SUPER_ADMIN: dropdown editable, pre-filled from filter */}
                            {!hideSedeField && (
                                <div style={{ ...cellStyle, gridColumn: 'span 2' }}>
                                    <label style={labelStyle}>Sedes (puedes seleccionar varias) *</label>
                                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', padding: '10px', border: '1px solid #d1d5db', borderRadius: '8px', background: '#f9fafb' }}>
                                        {availableSedesForCreate.map(t => (
                                            <label key={t.slug} style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px', background: 'white', padding: '5px 10px', borderRadius: '6px', border: '1px solid #e5e7eb', cursor: 'pointer' }}>
                                                <input
                                                    type="checkbox"
                                                    checked={form.sede.includes(t.slug)}
                                                    onChange={e => {
                                                        const val = t.slug;
                                                        setForm(f => {
                                                            const newSedes = e.target.checked
                                                                ? [...f.sede, val]
                                                                : f.sede.filter(s => s !== val);
                                                            return { ...f, sede: newSedes };
                                                        });
                                                    }}
                                                />
                                                {t.name}
                                            </label>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* SEDE_ADMIN: read-only sede label */}
                        {hideSedeField && fixedSedeName && (
                            <div style={{ fontSize: '12px', color: '#6b7280', display: 'flex', alignItems: 'center', gap: '6px' }}>
                                <Building2 size={13} />
                                Usuario será registrado en sede: <strong style={{ color: '#374151' }}>{fixedSedeName}</strong>
                            </div>
                        )}

                        {/* Botones */}
                        <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end', marginTop: '4px' }}>
                            <button type="button" onClick={onClose} style={{ padding: '10px 20px', borderRadius: '8px', border: '1px solid #d1d5db', background: 'white', cursor: 'pointer', fontWeight: 600, color: '#374151' }}>
                                Cancelar
                            </button>
                            <button type="submit" disabled={loading} style={{ padding: '10px 20px', borderRadius: '8px', border: 'none', background: 'linear-gradient(135deg,#4f46e5,#3b82f6)', color: 'white', cursor: loading ? 'wait' : 'pointer', fontWeight: 600, display: 'flex', alignItems: 'center', gap: '8px', opacity: loading ? 0.7 : 1 }}>
                                {loading ? <RotateCw size={15} style={{ animation: 'spin 1s linear infinite' }} /> : <Check size={15} />}
                                {loading ? 'Creando...' : 'Crear Usuario'}
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    );
};

// Helper: renders children into document.body to escape any scroll/overflow container
const Portal = ({ children }) => ReactDOM.createPortal(children, document.body);

// ─── WhatsApp Config Modal ──────────────────────────────────────────────────────
const WhatsAppConfigModal = ({ tenant, onClose, onSaved, showToast }) => {
    const { token } = useAuth();
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [testing, setTesting] = useState(false);
    const [testResult, setTestResult] = useState(null);
    const [copied, setCopied] = useState(false);
    const [showToken, setShowToken] = useState(false);
    const [error, setError] = useState(null);
    const [form, setForm] = useState({
        whatsapp_provider: 'evolution',
        wa_phone_number_id: '',
        wa_access_token: '',
        wa_verify_token: '',
        wa_business_account_id: '',
        evolution_instance: '',
        evolution_api_key: '',
        n8n_webhook_url: '',
        payment_verify_webhook: ''
    });

    // Load current config
    useEffect(() => {
        fetch(`${API_URL}/api/admin/tenants/${tenant.slug}`, {
            headers: { 'Authorization': `Bearer ${token}` }
        })
            .then(r => r.json())
            .then(data => {
                if (data.success) {
                    const t = data.tenant;
                    setForm({
                        whatsapp_provider: t.whatsapp_provider || 'evolution',
                        wa_phone_number_id: t.wa_phone_number_id || '',
                        wa_access_token: t.has_access_token ? '••••••••' : '',
                        wa_verify_token: t.wa_verify_token || '',
                        wa_business_account_id: t.wa_business_account_id || '',
                        evolution_instance: t.evolution_instance || '',
                        evolution_api_key: t.evolution_api_key || '',
                        n8n_webhook_url: t.n8n_webhook_url || '',
                        payment_verify_webhook: t.payment_verify_webhook || ''
                    });
                }
            })
            .catch(() => setError('Error cargando configuración'))
            .finally(() => setLoading(false));
    }, [tenant.slug, token]);

    const webhookUrl = `${API_URL}/webhook/meta?sede=${tenant.slug}`;

    const handleCopyWebhook = () => {
        navigator.clipboard.writeText(webhookUrl);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
    };

    const handleTestConnection = async () => {
        setTesting(true);
        setTestResult(null);
        try {
            const res = await fetch(`${API_URL}/api/admin/tenants/${tenant.slug}/whatsapp`, {
                method: 'PATCH',
                headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
                body: JSON.stringify({ ...form, _testOnly: true })
            });
            // Just try to hit the check-instance endpoint after saving
            setTestResult({ ok: res.ok, msg: res.ok ? 'Credenciales guardadas y válidas' : 'Error al verificar' });
        } catch {
            setTestResult({ ok: false, msg: 'Error de conexión' });
        } finally {
            setTesting(false);
        }
    };

    const handleSave = async () => {
        setError(null);
        setSaving(true);
        try {
            const res = await fetch(`${API_URL}/api/admin/tenants/${tenant.slug}/whatsapp`, {
                method: 'PATCH',
                headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
                body: JSON.stringify(form)
            });
            const data = await res.json();
            if (!res.ok) { setError(data.error || 'Error al guardar'); setSaving(false); return; }
            showToast(`✅ Configuración guardada para ${tenant.name}`, 'success');
            onSaved(data.tenant);
            onClose();
        } catch {
            setError('Error de conexión');
            setSaving(false);
        }
    };

    const inp = {
        width: '100%', padding: '9px 12px', border: '1px solid #d1d5db',
        borderRadius: '8px', fontSize: '14px', outline: 'none',
        boxSizing: 'border-box', background: 'white', minWidth: 0
    };
    const lbl = { display: 'block', fontSize: '13px', fontWeight: '600', color: '#374151', marginBottom: '5px' };
    const isOfficial = form.whatsapp_provider === 'official';

    return (
        <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.55)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9000, padding: '16px' }} onClick={onClose}>
            <div style={{ background: 'white', borderRadius: '16px', width: '100%', maxWidth: '540px', boxShadow: '0 25px 50px -12px rgba(0,0,0,0.3)', overflow: 'hidden', maxHeight: '92vh', display: 'flex', flexDirection: 'column' }} onClick={e => e.stopPropagation()}>

                {/* Header */}
                <div style={{ padding: '20px 24px', background: 'linear-gradient(135deg,#064e3b,#059669)', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <MessageSquare size={20} />
                        <div>
                            <div style={{ fontWeight: 700, fontSize: '17px' }}>Configuración WhatsApp</div>
                            <div style={{ fontSize: '12px', opacity: 0.85 }}>{tenant.name} · {tenant.slug}</div>
                        </div>
                    </div>
                    <button onClick={onClose} style={{ background: 'rgba(255,255,255,0.2)', border: 'none', borderRadius: '8px', padding: '4px 8px', cursor: 'pointer', color: 'white' }}>
                        <X size={18} />
                    </button>
                </div>

                {/* Body */}
                <div style={{ overflowY: 'auto', flex: 1, padding: '24px', display: 'flex', flexDirection: 'column', gap: '20px' }}>
                    {loading ? (
                        <div style={{ textAlign: 'center', padding: '40px', color: '#6b7280' }}>
                            <RotateCw size={24} style={{ animation: 'spin 1s linear infinite', margin: '0 auto 8px' }} />
                            <div>Cargando configuración...</div>
                        </div>
                    ) : (
                        <>
                            {error && (
                                <div style={{ background: '#fee2e2', border: '1px solid #fecaca', borderRadius: '8px', padding: '10px 12px', color: '#b91c1c', fontSize: '13px', display: 'flex', gap: '8px', alignItems: 'center' }}>
                                    <AlertCircle size={15} />{error}
                                </div>
                            )}

                            {/* Provider selector */}
                            <div>
                                <label style={lbl}>Proveedor de WhatsApp *</label>
                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
                                    {[
                                        { v: 'evolution', label: 'Evolution API', desc: 'Baileys / instancia propia', icon: <Zap size={16} /> },
                                        { v: 'official', label: 'API Oficial Meta', desc: 'Cloud API · Meta for Developers', icon: <MessageSquare size={16} /> }
                                    ].map(opt => (
                                        <button
                                            key={opt.v}
                                            type="button"
                                            onClick={() => setForm(f => ({ ...f, whatsapp_provider: opt.v }))}
                                            style={{
                                                padding: '12px 14px', borderRadius: '10px', cursor: 'pointer', textAlign: 'left',
                                                border: form.whatsapp_provider === opt.v ? '2px solid #059669' : '2px solid #e5e7eb',
                                                background: form.whatsapp_provider === opt.v ? '#f0fdf4' : 'white',
                                                transition: 'all 0.15s'
                                            }}
                                        >
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontWeight: 700, fontSize: '14px', color: form.whatsapp_provider === opt.v ? '#065f46' : '#374151', marginBottom: '4px' }}>
                                                {form.whatsapp_provider === opt.v
                                                    ? <Check size={14} color="#059669" />
                                                    : <div style={{ width: 14, height: 14, borderRadius: '50%', border: '2px solid #d1d5db' }} />}
                                                {opt.label}
                                            </div>
                                            <div style={{ fontSize: '11px', color: '#6b7280', paddingLeft: '22px' }}>{opt.desc}</div>
                                        </button>
                                    ))}
                                </div>
                            </div>

                            {/* ── OFFICIAL API FIELDS ── */}
                            {isOfficial && (
                                <>
                                    <div style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: '10px', padding: '14px 16px' }}>
                                        <div style={{ fontWeight: 700, fontSize: '13px', color: '#065f46', marginBottom: '8px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                                            <ExternalLink size={13} /> URL del Webhook (copiar en Meta for Developers)
                                        </div>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                            <code style={{ flex: 1, fontSize: '11px', background: 'white', border: '1px solid #bbf7d0', borderRadius: '6px', padding: '7px 10px', color: '#374151', wordBreak: 'break-all' }}>
                                                {webhookUrl}
                                            </code>
                                            <button
                                                onClick={handleCopyWebhook}
                                                style={{ flexShrink: 0, padding: '7px 12px', borderRadius: '7px', border: '1px solid #059669', background: copied ? '#059669' : 'white', color: copied ? 'white' : '#059669', cursor: 'pointer', fontSize: '12px', fontWeight: 600, display: 'flex', alignItems: 'center', gap: '5px', transition: 'all 0.2s' }}
                                            >
                                                {copied ? <Check size={13} /> : <Copy size={13} />}
                                                {copied ? 'Copiado' : 'Copiar'}
                                            </button>
                                        </div>
                                        <div style={{ fontSize: '11px', color: '#6b7280', marginTop: '6px' }}>
                                            Suscribirse al evento: <strong>messages</strong>
                                        </div>
                                    </div>

                                    <div>
                                        <label style={lbl}>Phone Number ID <span style={{ color: '#dc2626' }}>*</span></label>
                                        <input style={inp} value={form.wa_phone_number_id} onChange={e => setForm(f => ({ ...f, wa_phone_number_id: e.target.value }))} placeholder="Ej: 123456789012345" />
                                        <div style={{ fontSize: '11px', color: '#6b7280', marginTop: '4px' }}>Encuéntralo en Meta for Developers → Tu App → WhatsApp → Configuración → ID de número de teléfono</div>
                                    </div>

                                    <div>
                                        <label style={lbl}>Access Token (Token de acceso) <span style={{ color: '#dc2626' }}>*</span></label>
                                        <div style={{ position: 'relative' }}>
                                            <input
                                                style={{ ...inp, paddingRight: '40px', fontFamily: showToken ? 'inherit' : 'monospace' }}
                                                type={showToken ? 'text' : 'password'}
                                                value={form.wa_access_token}
                                                onChange={e => setForm(f => ({ ...f, wa_access_token: e.target.value }))}
                                                placeholder={form.wa_access_token === '••••••••' ? '(Token guardado — escribe uno nuevo para reemplazar)' : 'EAAxxxxx...'}
                                            />
                                            <button type="button" onClick={() => setShowToken(!showToken)} style={{ position: 'absolute', right: '10px', top: '9px', background: 'none', border: 'none', cursor: 'pointer', color: '#6b7280' }}>
                                                {showToken ? <EyeOff size={16} /> : <Eye size={16} />}
                                            </button>
                                        </div>
                                        <div style={{ fontSize: '11px', color: '#6b7280', marginTop: '4px' }}>Token permanente de sistema (nunca expira). Genera uno en Meta Business Suite.</div>
                                    </div>

                                    <div>
                                        <label style={lbl}>Verify Token (para el webhook) <span style={{ color: '#dc2626' }}>*</span></label>
                                        <input style={inp} value={form.wa_verify_token} onChange={e => setForm(f => ({ ...f, wa_verify_token: e.target.value }))} placeholder="Ej: mi_token_secreto_2024" />
                                        <div style={{ fontSize: '11px', color: '#6b7280', marginTop: '4px' }}>Cualquier texto secreto. Debes poner el mismo valor en Meta for Developers al registrar el webhook.</div>
                                    </div>

                                    <div>
                                        <label style={lbl}>WhatsApp Business Account ID (WABA ID) <span style={{ color: '#dc2626' }}>*</span></label>
                                        <input style={inp} value={form.wa_business_account_id} onChange={e => setForm(f => ({ ...f, wa_business_account_id: e.target.value }))} placeholder="Ej: 1019942661205891" />
                                        <div style={{ fontSize: '11px', color: '#6b7280', marginTop: '4px' }}>Requerido para leer plantillas. Búscalo en Meta for Developers → Configuración → ID de la cuenta de WhatsApp Business.</div>
                                    </div>
                                </>
                            )}

                            {/* ── EVOLUTION FIELDS ── */}
                            {!isOfficial && (
                                <>
                                    <div>
                                        <label style={lbl}>Nombre de instancia Evolution</label>
                                        <input style={inp} value={form.evolution_instance} onChange={e => setForm(f => ({ ...f, evolution_instance: e.target.value }))} placeholder="Ej: sede_cali" />
                                    </div>
                                    <div>
                                        <label style={lbl}>API Key de instancia (opcional)</label>
                                        <input style={inp} value={form.evolution_api_key} onChange={e => setForm(f => ({ ...f, evolution_api_key: e.target.value }))} placeholder="Deja vacío para usar la global" />
                                    </div>
                                </>
                            )}

                            {/* ── N8N WEBHOOK (always) ── */}
                            <div>
                                <label style={lbl}>URL Webhook de n8n (IA)</label>
                                <input style={inp} value={form.n8n_webhook_url} onChange={e => setForm(f => ({ ...f, n8n_webhook_url: e.target.value }))} placeholder="https://n8n.tudominio.com/webhook/..." />
                                <div style={{ fontSize: '11px', color: '#6b7280', marginTop: '4px' }}>Opcional. URL donde se reenvían los mensajes entrantes para procesamiento con IA.</div>
                            </div>

                            {/* ── PAYMENT WEBHOOK (always) ── */}
                            <div>
                                <label style={lbl}>URL Webhook de Verificación de Pagos (n8n)</label>
                                <input style={inp} value={form.payment_verify_webhook} onChange={e => setForm(f => ({ ...f, payment_verify_webhook: e.target.value }))} placeholder="https://n8n.tudominio.com/webhook/..." />
                                <div style={{ fontSize: '11px', color: '#6b7280', marginTop: '4px' }}>Opcional. URL a donde se envía la verificación cuando un cliente manda un comprobante de pago.</div>
                            </div>
                        </>
                    )}
                </div>

                {/* Footer */}
                {!loading && (
                    <div style={{ padding: '16px 24px', borderTop: '1px solid #f3f4f6', display: 'flex', gap: '10px', justifyContent: 'flex-end', flexShrink: 0, background: '#fafafa' }}>
                        <button type="button" onClick={onClose} style={{ padding: '10px 20px', borderRadius: '8px', border: '1px solid #d1d5db', background: 'white', cursor: 'pointer', fontWeight: 600, color: '#374151' }}>
                            Cancelar
                        </button>
                        <button
                            onClick={handleSave}
                            disabled={saving}
                            style={{ padding: '10px 24px', borderRadius: '8px', border: 'none', background: saving ? '#94a3b8' : 'linear-gradient(135deg,#065f46,#059669)', color: 'white', cursor: saving ? 'wait' : 'pointer', fontWeight: 700, display: 'flex', alignItems: 'center', gap: '8px' }}
                        >
                            {saving ? <RotateCw size={15} style={{ animation: 'spin 1s linear infinite' }} /> : <Check size={15} />}
                            {saving ? 'Guardando...' : 'Guardar Cambios'}
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
};

// ─── AdminPanel Principal ─────────────────────────────────────────────────────
const AdminPanel = ({ isMobile, onNavigateTab }) => {
    const { user, token, refreshUser } = useAuth();
    const { currentTenant } = useTenant();

    const [users, setUsers] = useState([]);
    const [tenants, setTenants] = useState([]);
    // Pre-seleccionar la sede activa del contexto global
    const [selectedSede, setSelectedSede] = useState(() => currentTenant?.slug || '');
    const [loading, setLoading] = useState(true);
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [showSedeModal, setShowSedeModal] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const [toast, setToast] = useState(null);
    const [confirmDelete, setConfirmDelete] = useState(null);
    const [syncingSlug, setSyncingSlug] = useState(null);
    const [cleaningVolume, setCleaningVolume] = useState(false);
    const [cleanupResult, setCleanupResult] = useState(null);
    const [cleanupDays, setCleanupDays] = useState(30);
    const [cleanupTypes, setCleanupTypes] = useState(['image', 'audio', 'video']);
    const [whatsappConfigTenant, setWhatsappConfigTenant] = useState(null); // tenant to configure

    const isSuperAdmin = user?.role === 'SUPER_ADMIN';
    const isSedeAdmin = user?.role === 'SEDE_ADMIN';

    const showToast = useCallback((msg, type = 'success') => {
        setToast({ msg, type });
        setTimeout(() => setToast(null), 3000);
    }, []);

    // Refresh user info to catch up with DB changes (like new sede assignments)
    useEffect(() => {
        refreshUser();
    }, [refreshUser]);
    useEffect(() => {
        fetch(`${API_URL}/api/admin/tenants`, { headers: { 'Authorization': `Bearer ${token}` } })
            .then(r => r.json())
            .then(data => { if (data.success) setTenants(data.tenants || []); })
            .catch(() => { });
    }, [token]);

    // Cargar usuarios
    const loadUsers = useCallback(async (sedeSlug) => {
        setLoading(true);
        try {
            const url = sedeSlug
                ? `${API_URL}/api/admin/users?sede=${encodeURIComponent(sedeSlug)}`
                : `${API_URL}/api/admin/users`;
            const res = await fetch(url, { headers: { 'Authorization': `Bearer ${token}` } });
            const data = await res.json();
            if (data.success) setUsers(data.users || []);
        } catch { }
        finally { setLoading(false); }
    }, [token]);

    // SEDE_ADMIN: auto-seleccionar su primera sede asignada
    useEffect(() => {
        if (isSedeAdmin && user?.tenants?.length > 0 && !selectedSede) {
            setSelectedSede(user.tenants[0].slug);
        }
    }, [isSedeAdmin, user, selectedSede]);

    useEffect(() => { loadUsers(selectedSede); }, [selectedSede, loadUsers]);

    const handleToggleStatus = async (userId, currentStatus) => {
        try {
            const res = await fetch(`${API_URL}/api/admin/users/${userId}/status`, {
                method: 'PATCH',
                headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
                body: JSON.stringify({ isActive: !currentStatus })
            });
            const data = await res.json();
            if (!res.ok) { showToast(data.error || 'Error', 'error'); return; }
            setUsers(prev => prev.map(u => u.id === userId ? { ...u, is_active: !currentStatus } : u));
            showToast(!currentStatus ? 'Usuario activado' : 'Usuario desactivado', 'success');
        } catch { showToast('Error de conexión', 'error'); }
    };

    const handleDelete = async (userId) => {
        setConfirmDelete(null);
        try {
            const res = await fetch(`${API_URL}/api/admin/users/${userId}`, {
                method: 'DELETE',
                headers: { 'Authorization': `Bearer ${token}` }
            });
            const data = await res.json();
            if (!res.ok) { showToast(data.error || 'Error al eliminar', 'error'); return; }
            setUsers(prev => prev.filter(u => u.id !== userId));
            showToast('Usuario eliminado', 'success');
        } catch { showToast('Error de conexión', 'error'); }
    };

    const handleSyncConversations = async (slug) => {
        setSyncingSlug(slug);
        try {
            const res = await fetch(`${API_URL}/api/admin/tenants/${slug}/sync-conversations`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` }
            });
            const data = await res.json();
            if (data.success) {
                showToast(`✅ ${data.imported} conversaciones sincronizadas (${data.skipped} omitidas)`, 'success');
            } else {
                showToast(data.error || 'Error sincronizando', 'error');
            }
        } catch (err) {
            showToast('Error de conexión', 'error');
        } finally {
            setSyncingSlug(null);
        }
    };

    const handleCleanupMedia = async () => {
        if (!window.confirm(`¿Estás seguro de que deseas limpiar el volumen?\nSe eliminarán los archivos (${cleanupTypes.join(', ')}) con más de ${cleanupDays} días de antigüedad.`)) return;
        
        setCleaningVolume(true);
        setCleanupResult(null);
        try {
            const res = await fetch(`${API_URL}/api/admin/cleanup-media`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
                body: JSON.stringify({ daysToKeep: cleanupDays, types: cleanupTypes })
            });
            const data = await res.json();
            if (data.success) {
                setCleanupResult(data.stats);
                showToast(`✅ Limpieza exitosa: ${data.stats.deletedFiles} archivos eliminados.`, 'success');
            } else {
                showToast(data.error || 'Error en la limpieza', 'error');
            }
        } catch (err) {
            showToast('Error de conexión', 'error');
        } finally {
            setCleaningVolume(false);
        }
    };

    const filteredUsers = users.filter(u => {
        const q = searchQuery.toLowerCase();
        return !q || u.name?.toLowerCase().includes(q) || u.username?.toLowerCase().includes(q) || u.email?.toLowerCase().includes(q);
    });

    const callerTenants = user?.tenants || [];

    return (
        <div style={{ flex: 1, minHeight: 0, overflowY: 'auto', background: '#f8fafc', padding: isMobile ? '12px' : '24px' }}>

            {/* Toast */}
            {toast && (
                <div style={{ position: 'fixed', top: '20px', right: '20px', zIndex: 9999, background: toast.type === 'success' ? '#10b981' : '#ef4444', color: 'white', padding: '12px 20px', borderRadius: '10px', boxShadow: '0 4px 20px rgba(0,0,0,0.15)', fontSize: '14px', fontWeight: 600, display: 'flex', alignItems: 'center', gap: '8px', maxWidth: '320px' }}>
                    {toast.type === 'success' ? <Check size={16} /> : <AlertCircle size={16} />}
                    {toast.msg}
                </div>
            )}

            {/* Confirmación eliminar */}
            {confirmDelete && (
                <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9000, padding: '16px' }}>
                    <div style={{ background: 'white', borderRadius: '12px', padding: '28px', maxWidth: '360px', width: '100%', boxShadow: '0 20px 40px rgba(0,0,0,0.2)' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px' }}>
                            <div style={{ background: '#fee2e2', padding: '10px', borderRadius: '10px' }}>
                                <Trash2 size={20} color="#dc2626" />
                            </div>
                            <div>
                                <div style={{ fontWeight: 700, fontSize: '16px', color: '#111' }}>Eliminar usuario</div>
                                <div style={{ color: '#6b7280', fontSize: '13px' }}>Esta acción no se puede deshacer</div>
                            </div>
                        </div>
                        <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
                            <button onClick={() => setConfirmDelete(null)} style={{ padding: '9px 18px', borderRadius: '8px', border: '1px solid #d1d5db', background: 'white', cursor: 'pointer', fontWeight: 600 }}>Cancelar</button>
                            <button onClick={() => handleDelete(confirmDelete)} style={{ padding: '9px 18px', borderRadius: '8px', border: 'none', background: '#dc2626', color: 'white', cursor: 'pointer', fontWeight: 600 }}>Eliminar</button>
                        </div>
                    </div>
                </div>
            )}

            {/* Encabezado */}
            <div style={{ marginBottom: '24px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px' }}>
                    <div style={{ background: 'linear-gradient(135deg,#1e1b4b,#4338ca)', borderRadius: '12px', padding: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <Users size={22} color="white" />
                    </div>
                    <div>
                        <h1 style={{ margin: 0, fontSize: '22px', fontWeight: 800, color: '#111827' }}>Gestión de Usuarios y Sedes</h1>
                        <p style={{ margin: 0, fontSize: '13px', color: '#6b7280' }}>
                            {isSuperAdmin ? 'Administración global de usuarios y configuración por sede' : 'Administración de tu sede'}
                        </p>
                    </div>
                </div>
            </div>

            {/* Banner IA Base General (Super Admin) */}
            {isSuperAdmin && (
                <div style={{ background: 'linear-gradient(135deg, #1e1b4b, #312e81)', color: 'white', borderRadius: '14px', padding: '20px 24px', marginBottom: '20px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '16px', boxShadow: '0 4px 14px rgba(30, 27, 75, 0.15)' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '14px', flex: 1, minWidth: '240px' }}>
                        <div style={{ background: 'rgba(255,255,255,0.15)', borderRadius: '12px', padding: '12px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <Globe size={24} color="#60a5fa" />
                        </div>
                        <div>
                            <h3 style={{ margin: 0, fontSize: '16px', fontWeight: 700, color: 'white' }}>IA Base de Datos General (Todas las Sedes)</h3>
                            <p style={{ margin: '4px 0 0 0', fontSize: '13px', color: '#c7d2fe' }}>
                                Administra productos, promociones, servicios y políticas corporativas compartidas con todas las sedes.
                            </p>
                        </div>
                    </div>
                    {onNavigateTab && (
                        <button onClick={() => onNavigateTab('ai_global')} style={{ background: 'white', color: '#1e1b4b', border: 'none', borderRadius: '10px', padding: '11px 22px', cursor: 'pointer', fontWeight: 700, fontSize: '14px', display: 'flex', alignItems: 'center', gap: '8px', boxShadow: '0 2px 8px rgba(0,0,0,0.1)', transition: 'all 0.2s' }}>
                            <Globe size={16} color="#2563eb" /> Abrir IA Base General
                        </button>
                    )}
                </div>
            )}

            {/* Barra de controles */}
            <div style={{ background: 'white', borderRadius: '12px', padding: '16px', marginBottom: '20px', boxShadow: '0 1px 3px rgba(0,0,0,0.06)', display: 'flex', flexWrap: 'wrap', gap: '12px', alignItems: 'center' }}>
                {/* Filtro de sede */}
                {(isSuperAdmin || (isSedeAdmin && callerTenants.length > 1)) && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flex: '1', minWidth: '180px' }}>
                        <Building2 size={16} color="#6b7280" />
                        <select 
                            value={selectedSede} 
                            onChange={e => setSelectedSede(e.target.value)}
                            style={{ border: '1px solid #e5e7eb', borderRadius: '8px', padding: '8px 12px', fontSize: '14px', outline: 'none', color: '#374151', cursor: 'pointer', background: 'white', flex: 1 }}
                        >
                            {isSuperAdmin && <option value="">Todas las sedes</option>}
                            {tenants
                                .filter(t => isSuperAdmin || callerTenants.some(ct => ct.slug === t.slug))
                                .map(t => <option key={t.slug} value={t.slug}>{t.name}</option>)
                            }
                        </select>
                    </div>
                )}

                {/* Búsqueda */}
                <div style={{ position: 'relative', flex: '1', minWidth: '200px' }}>
                    <Search size={15} color="#9ca3af" style={{ position: 'absolute', left: '10px', top: '50%', transform: 'translateY(-50%)' }} />
                    <input
                        placeholder="Buscar usuario..."
                        value={searchQuery}
                        onChange={e => setSearchQuery(e.target.value)}
                        style={{ width: '100%', padding: '8px 12px 8px 32px', border: '1px solid #e5e7eb', borderRadius: '8px', fontSize: '14px', outline: 'none', boxSizing: 'border-box', background: 'white' }}
                    />
                </div>

                {/* Refrescar */}
                <button onClick={() => loadUsers(selectedSede)} style={{ border: '1px solid #e5e7eb', borderRadius: '8px', padding: '8px 14px', background: 'white', cursor: 'pointer', color: '#374151', display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <RotateCw size={15} />
                </button>

                {/* Nueva Sede (solo SUPER_ADMIN) */}
                {isSuperAdmin && (
                    <button onClick={() => setShowSedeModal(true)} style={{ background: 'linear-gradient(135deg,#059669,#10b981)', color: 'white', border: 'none', borderRadius: '8px', padding: '9px 18px', cursor: 'pointer', fontWeight: 700, fontSize: '14px', display: 'flex', alignItems: 'center', gap: '8px', boxShadow: '0 4px 12px rgba(16,185,129,0.3)' }}>
                        <PlusCircle size={16} /> Nueva Sede
                    </button>
                )}

                {/* Nuevo usuario */}
                <button onClick={() => setShowCreateModal(true)} style={{ background: 'linear-gradient(135deg,#4f46e5,#3b82f6)', color: 'white', border: 'none', borderRadius: '8px', padding: '9px 18px', cursor: 'pointer', fontWeight: 700, fontSize: '14px', display: 'flex', alignItems: 'center', gap: '8px', boxShadow: '0 4px 12px rgba(79,70,229,0.3)' }}>
                    <UserPlus size={16} /> Nuevo Usuario
                </button>
            </div>

            {/* ── Mantenimiento (SUPER_ADMIN only) ── */}
            {isSuperAdmin && (
                <div style={{ background: 'white', borderRadius: '12px', padding: '16px', marginBottom: '20px', boxShadow: '0 1px 3px rgba(0,0,0,0.06)' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '14px' }}>
                        <AlertCircle size={16} color="#4338ca" />
                        <span style={{ fontWeight: 700, fontSize: '14px', color: '#111827' }}>Mantenimiento del Sistema</span>
                    </div>
                    
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '16px', alignItems: 'center' }}>
                            <div style={{ flex: 1, minWidth: '200px' }}>
                                <div style={{ fontWeight: 600, fontSize: '14px', color: '#374151', marginBottom: '8px' }}>Limpieza de Volumen (Archivos Antiguos)</div>
                                <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '12px' }}>
                                    Libera espacio en Railway eliminando multimedia antigua. Las imágenes asociadas a plantillas de Meta se conservan automáticamente.
                                </div>
                                <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
                                    <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px', color: '#4b5563' }}>
                                        Antigüedad:
                                        <select 
                                            value={cleanupDays} 
                                            onChange={e => setCleanupDays(Number(e.target.value))}
                                            style={{ padding: '4px 8px', borderRadius: '6px', border: '1px solid #d1d5db', outline: 'none' }}
                                        >
                                            <option value={7}>Más de 7 días</option>
                                            <option value={15}>Más de 15 días</option>
                                            <option value={30}>Más de 30 días</option>
                                            <option value={60}>Más de 60 días</option>
                                            <option value={90}>Más de 90 días</option>
                                        </select>
                                    </label>

                                    <div style={{ display: 'flex', gap: '12px', alignItems: 'center', fontSize: '13px', color: '#4b5563' }}>
                                        Tipos:
                                        {['image', 'video', 'audio', 'document'].map(type => (
                                            <label key={type} style={{ display: 'flex', alignItems: 'center', gap: '4px', cursor: 'pointer' }}>
                                                <input 
                                                    type="checkbox" 
                                                    checked={cleanupTypes.includes(type)} 
                                                    onChange={e => {
                                                        if (e.target.checked) setCleanupTypes([...cleanupTypes, type]);
                                                        else setCleanupTypes(cleanupTypes.filter(t => t !== type));
                                                    }} 
                                                />
                                                {type === 'image' ? 'Imágenes' : type === 'video' ? 'Videos' : type === 'audio' ? 'Audios' : 'Docs'}
                                            </label>
                                        ))}
                                    </div>
                                </div>
                            </div>
                            
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', alignItems: 'flex-end' }}>
                                <button
                                    onClick={handleCleanupMedia}
                                    disabled={cleaningVolume || cleanupTypes.length === 0}
                                    style={{ 
                                        display: 'flex', 
                                        alignItems: 'center', 
                                        gap: '8px', 
                                        padding: '10px 20px', 
                                        borderRadius: '8px', 
                                        border: 'none', 
                                        background: cleaningVolume || cleanupTypes.length === 0 ? '#94a3b8' : '#4338ca', 
                                        color: 'white', 
                                        cursor: cleaningVolume || cleanupTypes.length === 0 ? 'not-allowed' : 'pointer', 
                                        fontSize: '13px', 
                                        fontWeight: 700,
                                        boxShadow: '0 4px 10px rgba(67, 56, 202, 0.2)'
                                    }}
                                >
                                    {cleaningVolume ? <RotateCw size={15} style={{ animation: 'spin 1s linear infinite' }} /> : <Trash2 size={15} />}
                                    {cleaningVolume ? 'Limpiando...' : 'Ejecutar Limpieza'}
                                </button>
                                {cleanupResult && (
                                    <div style={{ fontSize: '12px', background: '#f0f9ff', padding: '6px 10px', borderRadius: '8px', border: '1px solid #bae6fd', color: '#0369a1' }}>
                                        <strong>Última:</strong> {cleanupResult.deletedFiles} borrados ({cleanupResult.recoveredMB} MB), {cleanupResult.protectedTemplateFiles || 0} imágenes de plantillas protegidas
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* ── Sedes (SUPER_ADMIN only) ── */}
            {isSuperAdmin && tenants.length > 0 && (
                <div style={{ background: 'white', borderRadius: '12px', padding: '16px', marginBottom: '20px', boxShadow: '0 1px 3px rgba(0,0,0,0.06)' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '14px' }}>
                        <Building2 size={16} color="#059669" />
                        <span style={{ fontWeight: 700, fontSize: '14px', color: '#111827' }}>Sedes registradas</span>
                        <span style={{ fontSize: '12px', color: '#6b7280', marginLeft: '4px' }}>({tenants.length})</span>
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                        {tenants.map(t => (
                            <div key={t.slug} style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '10px 14px', borderRadius: '10px', background: '#f8fafc', border: '1px solid #e2e8f0', flexWrap: 'wrap' }}>
                                <div style={{ flex: 1, minWidth: '120px' }}>
                                    <div style={{ fontWeight: 600, fontSize: '14px', color: '#111827' }}>{t.name}</div>
                                    <div style={{ fontSize: '11px', color: '#6b7280', fontFamily: 'monospace', display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' }}>
                                        {t.slug}
                                        {t.whatsapp_provider === 'official'
                                            ? <span style={{ background: '#dcfce7', color: '#15803d', borderRadius: '999px', padding: '1px 7px', fontWeight: 700, fontSize: '10px', fontFamily: 'inherit' }}>✅ API Oficial</span>
                                            : <span style={{ background: '#fef9c3', color: '#854d0e', borderRadius: '999px', padding: '1px 7px', fontWeight: 700, fontSize: '10px', fontFamily: 'inherit' }}>⚡ Evolution{t.evolution_instance ? ` · ${t.evolution_instance}` : ''}</span>
                                        }
                                    </div>
                                </div>
                                {/* WhatsApp Config button */}
                                <button
                                    onClick={() => setWhatsappConfigTenant(t)}
                                    title="Configurar proveedor de WhatsApp"
                                    style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '6px 12px', borderRadius: '8px', border: '1px solid #c7d2fe', background: '#eef2ff', color: '#4338ca', cursor: 'pointer', fontSize: '12px', fontWeight: 600 }}
                                >
                                    <Settings size={13} /> WhatsApp
                                </button>
                            </div>
                        ))}
                    </div>
                </div>
            )}


            {/* Tabla de usuarios */}
            <div style={{ background: 'white', borderRadius: '12px', boxShadow: '0 1px 3px rgba(0,0,0,0.06)', overflow: 'hidden' }}>
                {loading ? (
                    <div style={{ padding: '60px', textAlign: 'center', color: '#6b7280' }}>
                        <RotateCw size={28} style={{ animation: 'spin 1s linear infinite', margin: '0 auto 12px' }} />
                        <div>Cargando usuarios...</div>
                    </div>
                ) : filteredUsers.length === 0 ? (
                    <div style={{ padding: '60px', textAlign: 'center', color: '#6b7280' }}>
                        <Users size={40} style={{ margin: '0 auto 12px', opacity: 0.3 }} />
                        <div style={{ fontWeight: 600, fontSize: '16px', marginBottom: '4px' }}>Sin usuarios</div>
                        <div style={{ fontSize: '13px' }}>
                            {searchQuery ? 'No se encontraron resultados.' : 'No hay usuarios registrados en esta sede.'}
                        </div>
                    </div>
                ) : (
                    <div style={{ overflowX: 'auto' }}>
                        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                            <thead>
                                <tr style={{ backgroundColor: '#f9fafb', borderBottom: '1px solid #e5e7eb' }}>
                                    {['Usuario', 'Datos', 'Rol', 'Estado', 'Acciones'].map(h => (
                                        <th key={h} style={{ padding: '12px 16px', textAlign: 'left', fontSize: '12px', fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.04em' }}>{h}</th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {filteredUsers.map((u, idx) => (
                                    <tr key={u.id}
                                        style={{ borderBottom: idx < filteredUsers.length - 1 ? '1px solid #f3f4f6' : 'none', transition: 'background 0.15s' }}
                                        onMouseEnter={e => e.currentTarget.style.background = '#fafafa'}
                                        onMouseLeave={e => e.currentTarget.style.background = 'white'}
                                    >
                                        {/* Avatar + nombre */}
                                        <td style={{ padding: '14px 16px' }}>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                                <div style={{ width: '36px', height: '36px', borderRadius: '50%', flexShrink: 0, background: `linear-gradient(135deg, ${ROLE_COLORS[u.role]?.color || '#374151'}, #3b82f6)`, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white', fontWeight: 700, fontSize: '14px' }}>
                                                    {(u.name || u.username || '?').charAt(0).toUpperCase()}
                                                </div>
                                                <div>
                                                    <div style={{ fontWeight: 600, fontSize: '14px', color: '#111827' }}>{u.name || '—'}</div>
                                                    <div style={{ fontSize: '12px', color: '#6b7280' }}>@{u.username}</div>
                                                </div>
                                            </div>
                                        </td>

                                        {/* Email */}
                                        <td style={{ padding: '14px 16px' }}>
                                            <div style={{ fontSize: '13px', color: '#374151' }}>{u.email || '—'}</div>
                                            {u.last_login && (
                                                <div style={{ fontSize: '11px', color: '#9ca3af' }}>
                                                    Último acceso: {new Date(u.last_login).toLocaleDateString('es-CO')}
                                                </div>
                                            )}
                                        </td>

                                        {/* Rol */}
                                        <td style={{ padding: '14px 16px' }}><RoleBadge role={u.role} /></td>

                                        {/* Estado toggle */}
                                        <td style={{ padding: '14px 16px' }}>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                                <ToggleSwitch checked={u.is_active} onChange={() => handleToggleStatus(u.id, u.is_active)} disabled={u.id === user?.id} />
                                                <StatusBadge isActive={u.is_active} />
                                            </div>
                                        </td>

                                        {/* Acciones */}
                                        <td style={{ padding: '14px 16px' }}>
                                            {u.id !== user?.id ? (
                                                <button onClick={() => setConfirmDelete(u.id)} title="Eliminar usuario"
                                                    style={{ background: '#fee2e2', border: 'none', borderRadius: '7px', padding: '7px 10px', cursor: 'pointer', color: '#dc2626', display: 'flex', alignItems: 'center', gap: '5px', fontSize: '12px', fontWeight: 600 }}
                                                    onMouseEnter={e => e.currentTarget.style.background = '#fecaca'}
                                                    onMouseLeave={e => e.currentTarget.style.background = '#fee2e2'}
                                                >
                                                    <Trash2 size={14} /> Eliminar
                                                </button>
                                            ) : (
                                                <span style={{ fontSize: '12px', color: '#9ca3af', fontStyle: 'italic' }}>Tú</span>
                                            )}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}

                {/* Footer */}
                {!loading && filteredUsers.length > 0 && (
                    <div style={{ padding: '12px 16px', borderTop: '1px solid #f3f4f6', fontSize: '12px', color: '#6b7280' }}>
                        {filteredUsers.length} usuario{filteredUsers.length !== 1 ? 's' : ''}
                        {searchQuery && ` (filtrados)`}
                    </div>
                )}
            </div>

            {/* Modal crear usuario */}
            {showCreateModal && (
                <Portal>
                    <CreateUserModal
                        tenants={tenants}
                        callerRole={user?.role}
                        callerTenants={callerTenants}
                        currentSede={selectedSede}
                        onClose={() => setShowCreateModal(false)}
                        onCreated={() => loadUsers(selectedSede)}
                        showToast={showToast}
                    />
                </Portal>
            )}

            {/* Modal nueva sede */}
            {showSedeModal && (
                <Portal>
                    <CreateSedeModal
                        onClose={() => setShowSedeModal(false)}
                        onCreated={(newTenant) => {
                            setTenants(prev => [...prev, newTenant]);
                            setSelectedSede(newTenant.slug);
                        }}
                        showToast={showToast}
                    />
                </Portal>
            )}

            {/* Modal WhatsApp config */}
            {whatsappConfigTenant && (
                <Portal>
                    <WhatsAppConfigModal
                        tenant={whatsappConfigTenant}
                        onClose={() => setWhatsappConfigTenant(null)}
                        onSaved={(updatedTenant) => {
                            setTenants(prev => prev.map(t =>
                                t.slug === updatedTenant.slug ? { ...t, ...updatedTenant } : t
                            ));
                            setWhatsappConfigTenant(null);
                        }}
                        showToast={showToast}
                    />
                </Portal>
            )}

            <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
        </div>
    );
};

export default AdminPanel;
